<template>
  <!-- incoming:
  <div class="format-selector my-6">
    <div class="text-subtitle-1 font-weight-bold mb-3 primary--text">
      {{ $t('bookInfo.availableFormats') }}
    </div>
    <v-row dense>
      <v-col
        v-for="option in sortedFormats"
        :key="option.id"
        cols="12"
        sm="6"
        md="4"
        lg="3"
      >
        <v-card
          flat
          outlined
          :class="['format-card', { 'active': option.id === currentBookId }]"
          @click="selectFormat(option)"
        >
          <v-card-text class="d-flex flex-column align-center pa-4">
            <v-icon
              large
              :color="option.id === currentBookId ? 'primary' : 'grey darken-1'"
              class="mb-2"
            >
              {{ getIcon(option.format) }}
            </v-icon>
            <div 
              class="font-weight-bold text-center mb-1"
              :class="option.id === currentBookId ? 'primary--text' : 'grey--text text--darken-3'"
            >
              {{ getLabel(option.format) }}
            </div>
            <Price
              :book-price="option.price"
              :base-currency="option.currency"
              small
              class="mt-auto"
            />
          </v-card-text>
          
          <v-scale-transition>
            <v-icon
              v-if="option.id === currentBookId"
              small
              color="primary"
              class="active-check"
            >
              mdi-check-circle
            </v-icon>
          </v-scale-transition>
        </v-card>
      </v-col>
    </v-row>
-->
  <div
    class="format-selector"
    role="radiogroup"
    :aria-label="$t('bookInfo.chooseFormat')"
  >
    <ApolloQuery
      v-if="isLocalBook"
      :query="$options.query"
      :variables="{ title: searchTitle, itemsPerPage: SIBLING_FETCH_LIMIT }"
      :skip="!searchTitle"
      :prefetch="false"
    >
      <template #default="{ result: { data, error }, isLoading }">
        <div
          v-if="isLoading === 1"
          class="format-selector__row"
          aria-busy="true"
        >
          <v-skeleton-loader
            v-for="n in SKELETON_CHIP_COUNT"
            :key="n"
            type="chip"
            class="format-selector__skeleton"
          />
        </div>
        <div
          v-else
          class="format-selector__row"
        >
          <format-chip
            v-for="entry in entriesFor(data, error)"
            :key="entry.id"
            :entry="entry"
            :selected="entry.isCurrent"
            @select="navigateTo(entry)"
          />
        </div>
      </template>
    </ApolloQuery>

    <div
      v-else
      class="format-selector__row"
    >
      <format-chip
        :entry="currentEntry"
        :selected="true"
      />
    </div>
  </div>
</template>

<script>
/* incoming:
import Price from "@/components/Price.vue";
import { getFormatIcon } from "@/utils/BookFormatHelper";

export default {
  name: "FormatSelector",
  components: {
    Price,
  },
  props: {
    formats: {
      type: Array,
      required: true,
      default: () => [],
    },
    currentBookId: {
      type: String,
      required: true,
    },
  },
  computed: {
    sortedFormats() {
      // Sort priority: print (hardcover/paperback) > ebook > audiobook
      const priority = {
        hardcover: 1,
        paperback: 2,
        print: 3,
        epub: 4,
        pdf: 5,
        audio: 6
      };
      
      return [...this.formats].sort((a, b) => {
        const aType = this.getFormatType(a.format);
        const bType = this.getFormatType(b.format);
        return (priority[aType] || 99) - (priority[bType] || 99);
      });
    }
  },
  methods: {
    getFormatType(format) {
      if (!format) return 'unknown';
      const id = format._id || '';
      if (id.includes('epub')) return 'epub';
      if (id.includes('pdf')) return 'pdf';
      if (id.includes('lpf')) return 'audio';
      return id;
    },
    getIcon(format) {
      const type = this.getFormatType(format);
      if (type === 'hardcover') return 'mdi-book-open-page-variant';
      if (type === 'paperback') return 'mdi-book-variant-outline';
      return getFormatIcon(format);
    },
    getLabel(format) {
      const type = this.getFormatType(format);
      const translationKey = `formats.${type}`;
      return this.$te(translationKey) ? this.$t(translationKey) : type.toUpperCase();
    },
    selectFormat(option) {
      if (option.id !== this.currentBookId) {
        this.$emit('select', option.id);
      }
    }
  }
*/
import i18n from "@/i18n";
import LocalBookQueries from "@/graphql/LocalBookQueries";
import {
  getFormat,
  getFormatIcon,
  getFormatMimeType,
} from "@/utils/BookFormatHelper";
import { buildBookUrl } from "@/helpers/slugHelper";
import FormatChip from "@/components/FormatChip";

const SIBLING_FETCH_LIMIT = 12;
const SKELETON_CHIP_COUNT = 3;

// Print sub-formats encoded in titles (`Title - Print (Hardback)`). The backend
// only has one "print" BookFormat, so we differentiate these on the client.
const SUB_FORMATS = ["hardback", "hardcover", "paperback"];
const SUB_FORMAT_PATTERN = new RegExp(`\\((${SUB_FORMATS.join("|")})\\)\\s*$`, "i");

// Variant ranking — physical first (hardback → paperback → unspecified print),
// then ebook, pdf, audio. Hardcover collapses with hardback. Anything not
// listed is sorted last.
const VARIANT_RANK = {
  "print:hardback":  0,
  "print:hardcover": 0,
  "print:paperback": 1,
  "print":           2,
  "epub":            3,
  "pdf":             4,
  "audio":           5,
};
const VARIANT_RANK_UNKNOWN = Number.MAX_SAFE_INTEGER;

// Sub-format-aware icon overrides. Outside this map we fall back to
// getFormatIcon() from BookFormatHelper.
const SUB_FORMAT_ICONS = {
  hardback:  "mdi-book",
  hardcover: "mdi-book",
  paperback: "mdi-book-open-variant",
};

// Format suffixes that appear in real catalog titles. Both:
//   - Bracketed:   "Title [PRINT]", "Title (Hardback)"
//   - Dash-suffix: "Title - Print", "Title - Ebook (Hardback)"
// We strip these so siblings of one logical title compare equal.
// Run BRACKET first (handles trailing bracket on a dashed title), then DASH.
const FORMAT_KEYWORDS = "print|epub|pdf|audio|audiobook|hardback|hardcover|paperback|ebook|kindle";
const FORMAT_BRACKET_PATTERN = new RegExp(`\\s*[\\[(](?:${FORMAT_KEYWORDS})[\\])]\\s*`, "gi");
const FORMAT_DASH_SUFFIX_PATTERN = new RegExp(`\\s*[-–—]\\s*(?:${FORMAT_KEYWORDS})\\s*$`, "i");

function normalizeTitle(title) {
  if (!title) return "";
  return title
    .replace(FORMAT_BRACKET_PATTERN, " ")
    .replace(FORMAT_DASH_SUFFIX_PATTERN, "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}

function detectSubFormat(title) {
  if (!title) return null;
  const m = title.match(SUB_FORMAT_PATTERN);
  return m ? m[1].toLowerCase() : null;
}

function physicalSubFormat(formatId) {
  const id = (formatId || "").toLowerCase();
  if (["hardback", "hardcover", "paperback"].includes(id)) return id;
  return null;
}

function variantKey(formatId, subFormat) {
  return subFormat ? `${formatId}:${subFormat}` : formatId;
}

function variantRank(formatId, subFormat) {
  const key = variantKey(formatId, subFormat);
  return VARIANT_RANK[key] ?? VARIANT_RANK[formatId] ?? VARIANT_RANK_UNKNOWN;
}

function entryLabel(mimeType, subFormat) {
  if (subFormat) {
    const key = `formats.${subFormat}`;
    return i18n.te(key) ? i18n.t(key) : subFormat.charAt(0).toUpperCase() + subFormat.slice(1);
  }
  // `getFormat` expects a mimeType (e.g. "application/epub+zip"), not the
  // short `_id` ("epub"). Locale keys are mimeType-derived (formats."epub+zip").
  return getFormat(mimeType || "");
}

function entryIcon(subFormat, format) {
  if (subFormat && SUB_FORMAT_ICONS[subFormat]) return SUB_FORMAT_ICONS[subFormat];
  return getFormatIcon(format);
}

function buildEntry(book, currentBookId) {
  const mimeType = getFormatMimeType(book.format);
  const rawFormatId = (book.format && book.format._id) || mimeType || "";
  const subFormat = physicalSubFormat(rawFormatId) || detectSubFormat(book.title);
  const formatId = subFormat ? "print" : rawFormatId;
  return {
    id: book._id,
    book,
    mimeType,
    formatId,
    subFormat,
    variantKey: variantKey(formatId, subFormat),
    label: entryLabel(mimeType, subFormat),
    icon: entryIcon(subFormat, book.format),
    isCurrent: book._id === currentBookId,
  };
}

export default {
  name: "FormatSelector",
  components: { FormatChip },
  query: LocalBookQueries.getLocalBooksByTitle,
  props: {
    book: {
      type: Object,
      required: true,
    },
    isLocalBook: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      SIBLING_FETCH_LIMIT,
      SKELETON_CHIP_COUNT,
    };
  },
  computed: {
    searchTitle() {
      return normalizeTitle(this.book?.title) || null;
    },
    currentEntry() {
      return buildEntry(this.book, this.book?._id);
    },
  },
  methods: {
    entriesFor(data, error) {
      if (error || !data?.localBooks?.collection) return [this.currentEntry];

      const baseTitle = this.searchTitle;
      const seenIds = new Set();
      const byVariant = new Map();
      for (const b of data.localBooks.collection) {
        if (normalizeTitle(b.title) !== baseTitle) continue;
        if (seenIds.has(b._id)) continue;
        seenIds.add(b._id);
        const entry = buildEntry(b, this.book._id);
        // Dedupe variants. Current always wins; otherwise first seen wins.
        const existing = byVariant.get(entry.variantKey);
        if (!existing || entry.isCurrent) byVariant.set(entry.variantKey, entry);
      }

      const matches = Array.from(byVariant.values());
      // Always include the current book even if the API didn't return it.
      if (!matches.some(e => e.isCurrent)) matches.unshift(this.currentEntry);

      matches.sort(
        (a, b) => variantRank(a.formatId, a.subFormat) - variantRank(b.formatId, b.subFormat)
      );
      return matches;
    },
    navigateTo(entry) {
      if (entry.isCurrent) return;
      const url = buildBookUrl(entry.book);
      if (url && url !== this.$route.fullPath) this.$router.push(url);
    },
  },
};
</script>

<style scoped lang="scss">
/* incoming:
.format-card {
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  border-width: 2px !important;
  height: 100%;
  position: relative;
  border-radius: 12px !important;
  background-color: var(--v-background-base) !important;

  &:hover {
    border-color: var(--v-primary-base) !important;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  }

  &.active {
    border-color: var(--v-primary-base) !important;
    background-color: rgba(var(--v-primary-base), 0.05) !important;
  }
}

.active-check {
  position: absolute;
  top: 8px;
  right: 8px;
*/
@import "@/styles/variables.scss";

$row-gap: 8px;
$row-margin-top: 4px;
$row-margin-bottom: 16px;

$skeleton-width: 120px;
$skeleton-height: 40px;

.format-selector {
  margin-top: $row-margin-top;
  margin-bottom: $row-margin-bottom;
}

.format-selector__row {
  display: flex;
  flex-wrap: wrap;
  gap: $row-gap;
  align-items: stretch;
}

.format-selector__skeleton ::v-deep .v-skeleton-loader__chip {
  width: $skeleton-width;
  height: $skeleton-height;
}
</style>
